import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Eye, Loader2 } from 'lucide-react'
import { formsApi } from '@/api/formsApi'
import { procedureTemplatesApi } from '@/api/procedureTemplatesApi'
import { FormRenderer } from '@/components/renderer/FormRenderer'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { useTenant } from '@/providers/TenantProvider'
import type { CgtUser, FormSubmission } from '@/types/cgt'

/** Signs as a user in every group, as the Form Editor's preview does, so signature fields render as they will. */
const PREVIEW_USER: CgtUser = { id: 'preview', name: 'Preview User', groups: ['MedicalDirector', 'LabTechnician'] }

/** A throwaway draft for the preview — never persisted. */
function previewSubmission(formDefinitionId: string): FormSubmission {
  const now = new Date().toISOString()
  return {
    id: 'preview',
    formDefinitionId,
    patientMrn: 'PREVIEW',
    status: 'draft',
    values: {},
    createdAt: now,
    updatedAt: now,
  }
}

/**
 * A read-only look at the procedure an event runs (z8nrz7fb9k): its forms in
 * order, each rendered as the Form Editor's Preview renders it. Nothing can be
 * filled in and nothing is saved — the fields sit in a disabled fieldset, and
 * the renderer's preview mode never calls the API either way.
 */
export function ProcedurePreviewDialog({
  procedure,
  onClose,
  context,
}: {
  /** The procedure to preview; null closes the dialog. */
  procedure: { id: string; name: string } | null
  onClose: () => void
  /** What it is linked to, e.g. "Calibration · Monthly · Biosafety cabinets". */
  context?: string
}) {
  const { selectedTenant } = useTenant()
  const templateQuery = useQuery({
    queryKey: ['procedure-template', procedure?.id],
    enabled: !!procedure,
    queryFn: () => procedureTemplatesApi.get(procedure!.id),
  })
  const steps = useMemo(
    () => [...(templateQuery.data?.steps ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
    [templateQuery.data],
  )
  const [stepId, setStepId] = useState<string | null>(null)
  useEffect(() => setStepId(null), [procedure?.id])
  const step = steps.find(s => s.stepId === stepId) ?? steps[0] ?? null

  const definitionQuery = useQuery({
    queryKey: ['form-definition', step?.formDefinitionId],
    enabled: !!step,
    queryFn: () => formsApi.getDefinition(step!.formDefinitionId),
  })
  const [submission, setSubmission] = useState<FormSubmission | null>(null)
  useEffect(() => {
    setSubmission(step ? previewSubmission(step.formDefinitionId) : null)
  }, [step])

  return (
    <Dialog open={procedure !== null} onOpenChange={open => !open && onClose()}>
      <DialogContent className='flex max-h-[90vh] flex-col gap-3 sm:max-w-5xl'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <Eye className='h-4 w-4 text-muted-foreground' />
            {procedure?.name ?? 'Procedure'}
          </DialogTitle>
          <DialogDescription>
            {context ? `${context} · ` : ''}Preview only — nothing can be filled in or saved here.
          </DialogDescription>
        </DialogHeader>

        {templateQuery.isLoading ? (
          <p className='flex items-center gap-2 py-10 text-sm text-muted-foreground'>
            <Loader2 className='h-4 w-4 animate-spin' /> Loading the procedure…
          </p>
        ) : templateQuery.isError ? (
          <p className='py-10 text-sm text-destructive'>The procedure could not be loaded.</p>
        ) : steps.length === 0 ? (
          <p className='py-10 text-sm text-muted-foreground'>This procedure has no forms yet.</p>
        ) : (
          <>
            {steps.length > 1 && (
              <div className='flex flex-wrap gap-1.5' role='tablist' aria-label='Forms in this procedure'>
                {steps.map((s, i) => (
                  <button
                    key={s.stepId}
                    type='button'
                    role='tab'
                    aria-selected={s.stepId === step?.stepId}
                    onClick={() => setStepId(s.stepId)}
                    className={cn(
                      'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                      s.stepId === step?.stepId
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-gray-200 text-gray-600 hover:bg-gray-50',
                    )}
                  >
                    {i + 1}. {s.formName}
                  </button>
                ))}
              </div>
            )}
            <div className='min-h-0 flex-1 overflow-y-auto rounded-lg border bg-muted/30 p-4'>
              {definitionQuery.isLoading || !submission ? (
                <p className='flex items-center gap-2 py-10 text-sm text-muted-foreground'>
                  <Loader2 className='h-4 w-4 animate-spin' /> Loading the form…
                </p>
              ) : definitionQuery.isError || !definitionQuery.data ? (
                <p className='py-10 text-sm text-destructive'>This form could not be loaded.</p>
              ) : (
                // Disabled fieldset: every input, select and button in the form is
                // inert, so the preview cannot be filled in (the renderer would
                // otherwise let you, as the Form Editor's test preview does).
                <fieldset disabled className='contents' data-testid='procedure-preview-form'>
                  <div className='rounded-xl border bg-card p-6 shadow-sm'>
                    <FormRenderer
                      definition={definitionQuery.data}
                      submission={submission}
                      currentUser={PREVIEW_USER}
                      onSubmissionUpdated={setSubmission}
                      preview
                      hideComplete
                      tenantTimezone={selectedTenant?.timezone}
                    />
                  </div>
                </fieldset>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
